import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { WorkHoursInterval } from '@elfsight/widget-business-hours';
import {
  getSpecialDaysFromDate,
  getWeekFromDate
} from '@elfsight/widget-business-hours';
import { useSettings } from '@wu/widget-core';
import type { Interval } from 'date-fns';
import { addDays, isBefore, isSameDay, startOfDay } from 'date-fns';
import { isCalendarIntegrationEnabled } from '../../common/is-calendar-integration-enabled';
import { useEventContext } from '../event-provider';
import { useCalendarReservedTimeslotsQuery } from '../google-calendar/use-calendar-reserved-timeslots.query';
import { useSettingsSpecialIntervals } from '../hours-settings/use-settings-special-intervals';
import { useSettingsWeek } from '../hours-settings/use-settings-week';
import { useLockTimeslot } from '../locked-timeslots/use-lock-timeslots.mutation';
import { useLockedTimeslotsQuery } from '../locked-timeslots/use-locked-timeslots.query';
import { useServices } from '../service/services-provider';
import { createTimeslots } from './utils/create-timeslots';

export type Timeslot = {
  from: Date;
  to: Date;
  reserved: boolean;
  past: boolean;
  locked: boolean;
};
export type Day = {
  date: Date;
  hours: WorkHoursInterval[];
};
export type DayWithTimeslots = Day & {
  slots: Timeslot[];
};
type DaysWithTimeslots = DayWithTimeslots[];

type DatetimeContextI = {
  getDayTimeslots: (
    givenDay?: DayWithTimeslots,
    withPast?: boolean,
    withReserved?: boolean,
    withLocked?: boolean
  ) => Timeslot[] | undefined;
  isDayAvailable: (
    givenDay?: DayWithTimeslots,
    withPast?: boolean,
    withReserved?: boolean,
    withLocked?: boolean
  ) => boolean;
  selectedTimeslot: Timeslot | undefined;
  setSelectedTimeslot: (timeslot: Timeslot | undefined) => void;
  selectedDay: DayWithTimeslots | undefined;
  setSelectedDay: (timeslot: DayWithTimeslots | undefined) => void;
  setFirstAvailableDay: () => void;
  days: DayWithTimeslots[];
  interval: Interval;
  isInitialLoading: boolean;
};

const DatetimeContext = createContext<DatetimeContextI | null>(null);

export function DatetimeProvider({ children }: PropsWithChildren) {
  const {
    editMode,
    minAdvanceMinutes,
    maxAdvanceDays,
    integrationGoogleCalendarCalendar,
    integrationGoogleCalendarAuth
  } = useSettings();
  const { currentService } = useServices();
  const specialIntervals = useSettingsSpecialIntervals();
  const weekConfig = useSettingsWeek();
  const { publish, subscribe } = useEventContext();
  const isGCEnabled = isCalendarIntegrationEnabled({
    integrationGoogleCalendarCalendar,
    integrationGoogleCalendarAuth
  });

  const [selectedDay, setSelectedDay] = useState<
    DayWithTimeslots | undefined
  >();
  const [selectedTimeslot, setSelectedTimeslot] = useState<
    Timeslot | undefined
  >();

  const interval = useMemo(
    () => ({
      start: startOfDay(new Date()),
      end: addDays(startOfDay(new Date()), maxAdvanceDays)
    }),
    [maxAdvanceDays]
  );

  const { data: reservedTimeslots, isLoading } =
    useCalendarReservedTimeslotsQuery(interval);

  const { data: lockedTimeslots } = useLockedTimeslotsQuery(interval);

  const lockTimeslot = useLockTimeslot();

  const days: Day[] = useMemo(() => {
    const specialDays = getSpecialDaysFromDate(
      specialIntervals,
      interval.start
    );

    let days: Day[] = getWeekFromDate(weekConfig, interval.start);

    let lastDay = days.at(-1);
    if (lastDay) {
      while (lastDay && isBefore(lastDay.date, interval.end)) {
        days = days.concat(
          getWeekFromDate(weekConfig, addDays(lastDay.date, 1))
        );
        lastDay = days.at(-1);
      }
    }

    specialDays.forEach((specialDay) => {
      const index = days.findIndex((weekDay) =>
        isSameDay(weekDay.date, specialDay.date)
      );

      if (index !== -1) {
        days[index] = specialDay;
      }
    });

    return days.filter(({ date }) => isBefore(date, interval.end));
  }, [specialIntervals, weekConfig, interval]);

  const daysWithTimeslots: DaysWithTimeslots = useMemo(() => {
    // const createDaysWithTimeslots = useCallback(() => {
    return days.map((day) => {
      const slots = createTimeslots(
        day.hours,
        currentService?.duration,
        minAdvanceMinutes,
        reservedTimeslots,
        lockedTimeslots
      );
      return { ...day, slots };
    });
  }, [
    days,
    reservedTimeslots,
    lockedTimeslots,
    minAdvanceMinutes,
    currentService?.duration
  ]);

  // const [daysWithTimeslots, setDaysWithTimeslots] = useState<DaysWithTimeslots>(
  //   createDaysWithTimeslots
  // );

  // useEffect(() => {
  //   setDaysWithTimeslots(createDaysWithTimeslots);
  // }, [createDaysWithTimeslots]);
  //
  // useInterval(() => {
  //   setDaysWithTimeslots(createDaysWithTimeslots);
  // }, 60 * 1000);

  const getDayTimeslots = (
    givenDay?: DayWithTimeslots,
    withPast = false,
    withReserved = false,
    withLocked = false
  ) => {
    if (!givenDay) {
      return [];
    }

    const day = daysWithTimeslots.find(({ date }) =>
      isSameDay(givenDay.date, date)
    );

    if (!day) {
      return [];
    }

    return day.slots.filter(
      ({ reserved, locked, past }) =>
        (!reserved || withReserved) &&
        (!past || withPast) &&
        (!locked || withLocked)
    );
  };

  const isDayAvailable = (
    givenDay: DayWithTimeslots,
    withPast = false,
    withReserved = false,
    withLocked = false
  ) => {
    if (!givenDay) {
      return false;
    }
    const timeslots = getDayTimeslots(
      givenDay,
      withPast,
      withReserved,
      withLocked
    );
    return timeslots?.length > 0;
  };

  const handleSetSelectedDay = (day: DayWithTimeslots | undefined) => {
    setSelectedDay(day);
  };

  const handleSetSelectedTimeslot = (timeslot: Timeslot | undefined) => {
    setSelectedTimeslot(timeslot);
    if (timeslot && isGCEnabled) {
      lockTimeslot.mutate(timeslot);
    }
  };

  const setFirstAvailableDay = () => {
    const day = daysWithTimeslots.find((day) =>
      day.slots.some(({ past, reserved }) => !past && !reserved)
    );
    setSelectedDay(day);
    publish('firstAvailableDaySet', day);
  };

  useEffect(() => {
    if (!isLoading && !selectedDay && currentService?.duration) {
      setFirstAvailableDay();
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [isLoading, selectedDay, currentService?.duration]);

  useEffect(() => {
    const unsubscribe = subscribe('resetAll', () => {
      setSelectedDay(undefined);
      setSelectedTimeslot(undefined);
    });

    return () => {
      unsubscribe();
    };
    // TODO: поправить зависимости
  }, []);

  useEffect(() => {
    if (isGCEnabled && editMode) {
      publish('resetAll');
    }
    // TODO: поправить зависимости
  }, [isGCEnabled, editMode]);

  return (
    <DatetimeContext.Provider
      value={{
        getDayTimeslots,
        // TODO: разобраться с типами
        isDayAvailable,
        selectedDay,
        days: daysWithTimeslots,
        interval,
        setSelectedDay: handleSetSelectedDay,
        selectedTimeslot,
        setSelectedTimeslot: handleSetSelectedTimeslot,
        setFirstAvailableDay,
        isInitialLoading: isLoading
      }}
    >
      {children}
    </DatetimeContext.Provider>
  );
}

export function useDatetime() {
  const context = useContext(DatetimeContext);

  if (context === null) {
    throw new Error(`useDatetime should be used within DatetimeProvider`);
  }

  return context;
}
